import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, HelpCircle } from 'lucide-react';
import { useGlobalFontSize } from '../../../context/GlobalFontSizeContext';

const PaywallCard = ({ plan, isCurrentPlan, isDowngrade = false, downgradeDisabled = false, lockedLabel = null, onChoose, purchasing }) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState('left');
  const tooltipRef = useRef(null);
  const tooltipBtnRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const isTouchDeviceRef = useRef(false);

  useEffect(() => {
    isTouchDeviceRef.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  useEffect(() => {
    if (!showTooltip) return;

    const handleTouchOutside = (event) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target) &&
        tooltipBtnRef.current &&
        !tooltipBtnRef.current.contains(event.target)
      ) {
        setShowTooltip(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowTooltip(false);
      }
    };

    document.addEventListener('touchstart', handleTouchOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('touchstart', handleTouchOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showTooltip]);

  useEffect(() => {
    if (showTooltip && tooltipBtnRef.current) {
      const rect = tooltipBtnRef.current.getBoundingClientRect();
      const spaceOnRight = window.innerWidth - rect.right;
      setTooltipPosition(spaceOnRight < 300 ? 'right' : 'left');
    }
  }, [showTooltip]);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const getButtonText = () => {
    if (isCurrentPlan) {
      return t('subscription.paywall.currentPlan');
    }

    if (lockedLabel) {
      return lockedLabel;
    }

    if (purchasing) {
      return t('subscription.paywall.processing');
    }

    if (isDowngrade) {
      return t('subscription.paywall.downgradeTo', { plan: plan.name });
    }

    return plan.cta;
  };

  const showCancelAnytime = plan.highlighted && !isCurrentPlan && !isDowngrade;

  return (
    <div
      className={`relative flex-1 basis-[280px] min-w-0 max-w-[400px] rounded-[12px] pt-[16px] pb-[16px] px-[10px] transition-all duration-300 flex flex-col bg-white dark:bg-[#1d1d1d] ${
        isCurrentPlan
          ? 'border-2 border-bloom-green shadow-[0_1px_2px_rgba(16,24,40,0.06),0_12px_32px_-12px_rgba(16,24,40,0.16)] dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10'
          : plan.highlighted
            ? 'border border-[#24292f] dark:border-[#8b949e] shadow-[0_2px_4px_rgba(16,24,40,0.08),0_20px_40px_-12px_rgba(16,24,40,0.26)] dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10'
            : 'border border-[#E8EBE9] dark:border-[#30363d] shadow-[0_1px_2px_rgba(16,24,40,0.06),0_12px_32px_-12px_rgba(16,24,40,0.16)] dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/10'
      }`}
    >
      <div className='px-[12px]'>
        <div className="flex flex-wrap items-start justify-center text-center gap-x-[8px] gap-y-[6px] mb-[8px]">
          <h2
            className="font-['Inter'] font-semibold text-[#111111] dark:text-white min-w-0 break-words"
            style={{ fontSize }}
          >
            {plan.name}
          </h2>
          {isCurrentPlan && (
            <span
              className="shrink-0 inline-block bg-bloom-green text-white px-[12px] py-[6px] rounded-[6px] font-['Inter'] font-semibold leading-none"
              style={{ fontSize: fontSize - 4 }}
            >
              {t('subscription.paywall.current')}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-baseline justify-center gap-[6px]">
          <span
            className="font-['Inter'] font-medium text-[#111111] dark:text-white"
            style={{ fontSize: fontSize + 10 }}
          >
            {plan.price}
          </span>
          {plan.period && (
            <span
              className="font-['Inter'] font-normal text-[#6B7280] dark:text-[#8b949e]"
              style={{ fontSize: fontSize - 2 }}
            >
              {plan.period}
            </span>
          )}
        </div>
      </div>

      {/* Splitter, 16px of breathing room above and below */}
      <div className="my-[16px] mx-[12px] h-[1px] bg-[#E8EBE9] dark:bg-[#30363d]" />

      <div className="mb-[24px] flex-grow">
        <h3
          className="font-['Inter'] font-bold text-[#5D625F] dark:text-[#8b949e] mb-[16px] px-[12px]"
          style={{ fontSize: fontSize - 2 }}
        >
          {t('subscription.paywall.whatsIncluded')}
        </h3>
        <ul className="space-y-[12px]">
          {plan.features.map((feature, index) => {
            const featureText = typeof feature === 'string' ? feature : feature.text;
            const featureId = typeof feature === 'object' ? feature.id : null;
            const isNew = typeof feature === 'object' && feature.isNew;
            const hasTooltip = typeof feature === 'object' && feature.hasTooltip;
            const tooltipText = typeof feature === 'object' ? feature.tooltipText : null;

            return (
              <li key={featureId || index} className="relative flex items-start gap-[10px]">
                <Check size={16} className="text-bloom-green flex-shrink-0 mt-[3px]" />
                <div className="flex items-center gap-[8px] flex-wrap min-w-0 break-words">
                  <span
                    className="font-['Inter'] font-normal leading-[20px] text-[#282A29] dark:text-[#c9d1d9]"
                    style={{ fontSize: fontSize - 2 }}
                  >
                    {featureText}
                  </span>
                  {hasTooltip && (
                    <div
                      className="inline-flex"
                      onMouseEnter={() => {
                        if (!isTouchDeviceRef.current) {
                          hoverTimeoutRef.current = setTimeout(() => setShowTooltip(true), 150);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isTouchDeviceRef.current) {
                          clearTimeout(hoverTimeoutRef.current);
                          setShowTooltip(false);
                        }
                      }}
                    >
                      <button
                        ref={tooltipBtnRef}
                        onClick={() => setShowTooltip((prev) => !prev)}
                        className="text-[#828382] dark:text-[#8b949e] hover:text-[#282A29] dark:hover:text-white transition-colors p-[2px] rounded-full focus:outline-none focus:ring-2 focus:ring-bloom-green focus:ring-offset-1"
                        aria-label="Smart Alerts info"
                        aria-expanded={showTooltip}
                      >
                        <HelpCircle size={14} />
                      </button>
                      {showTooltip && (
                        <div
                          ref={tooltipRef}
                          className={`absolute left-0 right-0 ${tooltipPosition === 'right' ? 'sm:left-auto sm:right-0' : 'sm:right-auto'} sm:w-72 max-w-full top-full mt-2 z-50 bg-[#252525] border border-[#3a3c3b] rounded-lg shadow-xl`}
                          role="tooltip"
                        >
                          <div className={`hidden sm:block absolute -top-2 ${tooltipPosition === 'right' ? 'right-3' : 'left-3'} w-3 h-3 bg-[#252525] border-l border-t border-[#3a3c3b] transform rotate-45`} />
                          <div className="px-3 py-2 border-b border-[#3a3c3b]">
                            <span className="text-white text-sm font-medium font-['Inter']">Smart Alerts</span>
                          </div>
                          <div className="px-3 py-2">
                            <p className="text-gray-300 text-xs leading-relaxed font-['Inter']">
                              {tooltipText}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {isNew && (
                    <span className="bg-[#a855f7] text-white font-['Inter'] font-medium text-[10px] px-[8px] py-[2px] rounded-[4px]">
                      {t('subscription.paywall.newTag')}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="mt-auto">
        <button
          type="button"
          onClick={() => onChoose(plan)}
          disabled={isCurrentPlan || Boolean(lockedLabel) || downgradeDisabled || purchasing}
          className={`w-full py-[14px] px-[16px] rounded-[8px] font-['Inter'] font-medium transition-all duration-200 ${
            isCurrentPlan || lockedLabel
              ? 'bg-white dark:bg-transparent border border-[#C5C9C7] dark:border-[#5d625f] text-[#828382] dark:text-[#8b949e] cursor-default'
              : purchasing
                ? 'bg-bloom-green/60 text-white cursor-not-allowed'
                : isDowngrade
                  ? `bg-transparent border border-[#ef4444] dark:border-[#ff6b6b] text-[#ef4444] dark:text-[#ff6b6b] ${
                      downgradeDisabled
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-[#ef4444]/5 dark:hover:bg-[#ff6b6b]/10'
                    }`
                  : 'bg-bloom-green text-white hover:bg-bloom-green-hover'
          }`}
          style={{ fontSize: fontSize - 2 }}
        >
          {getButtonText()}
        </button>

        <p
          className={`mt-[12px] min-h-[18px] text-center font-['Inter'] text-[#828382] dark:text-[#8b949e] ${
            showCancelAnytime ? 'visible' : 'invisible'
          }`}
          style={{ fontSize: fontSize - 4 }}
          aria-hidden={!showCancelAnytime}
        >
          {t('subscription.paywall.cancelAnytime')}
        </p>
      </div>
    </div>
  );
};

export default PaywallCard;
